import { useEffect } from 'react';

/**
 * iOS 26 Safari "Liquid Glass" Support
 * 
 * Safari 26 automatically samples the background-color at the top edge of the viewport
 * and uses it to tint the status bar and URL bar glass areas.
 * 
 * This hook optionally updates the theme-color meta tag for older Safari versions,
 * though iOS 26 largely relies on pixel sampling instead.
 * 
 * To enable the glass effect, ensure your page has:
 * 1. A fixed inset-0 background element
 * 2. An OPAQUE color at the top (position 0%) of any gradient
 * 3. No transparent layers blocking Safari's sampling
 */
export function useDynamicGlassBackground(
  color: string | undefined,
  options?: {
    updateMetaThemeColor?: boolean;
  }
) {
  const { updateMetaThemeColor = false } = options || {};

  useEffect(() => {
    if (!color || !updateMetaThemeColor) return;

    // Update meta theme-color for older Safari versions
    let metaThemeColor = document.querySelector('meta[name="theme-color"]') as HTMLMetaElement | null;
    
    if (!metaThemeColor) {
      metaThemeColor = document.createElement('meta');
      metaThemeColor.name = 'theme-color';
      document.head.appendChild(metaThemeColor);
    }
    
    const previousColor = metaThemeColor.content;
    metaThemeColor.content = color;

    return () => {
      if (metaThemeColor) {
        metaThemeColor.content = previousColor || '#000000';
      }
    };
  }, [color, updateMetaThemeColor]);
}
